import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckMarkIcon } from '@svgs'
import { IProperty } from 'src/interfaces/IProperty'

interface IPropertyParameters {
  parameters: IProperty['parametersSection']
}

// A listing's parameters, used by both the announcement page and the
// announcement modal: a two-column list of "label — value" rows, and the
// yes/no ones as check chips under it. Only meaningful values render: false
// booleans, nulls, empty strings and empty arrays are skipped so a land plot
// no longer shows apartment-only chips (client feedback, roadmap 15.8).
// Option labels live under a namespace matching the parameter key
// (heating.central, districtType.residential, ...) with a couple of historical
// exceptions mapped here.
const VALUE_NAMESPACE_EXCEPTIONS: Record<string, string> = {
  landPurpose: 'step3.landPurpose_',
  availabilityOfRepairs: 'repairsAvailability.',
  // the kinds of a commercial property are the step 1 choices
  commercialType: 'step1.',
}

// Shared with the compare table: same label the select showed, raw fallback.
export const translateParamValue = (
  t: (key: string, options?: { defaultValue: string }) => string,
  key: string,
  rawValue: string,
): string => {
  const clean = rawValue.startsWith(`${key}.`) ? rawValue.slice(key.length + 1) : rawValue
  const prefix = VALUE_NAMESPACE_EXCEPTIONS[key] ?? `${key}.`
  return t(`${prefix}${clean.toLowerCase()}`, { defaultValue: clean })
}

// the areas carry their unit ("54 м²"); the other numbers say it in the label
const AREAS = new Set(['totalArea', 'livingArea', 'kitchenArea'])

const PropertyParameters: FC<IPropertyParameters> = ({ parameters }) => {
  const { t } = useTranslation()

  // Values are stored as raw option codes ("residential", "central"); show the
  // same label the select showed, falling back to the raw value for free-form
  // fields (cadastral number, numbers, year ranges).
  const translateValue = (key: string, rawValue: string): string => {
    const clean = rawValue.startsWith(`${key}.`) ? rawValue.slice(key.length + 1) : rawValue
    const prefix = VALUE_NAMESPACE_EXCEPTIONS[key] ?? `${key}.`
    return t(`${prefix}${clean.toLowerCase()}`, { defaultValue: clean })
  }

  // the kind of a commercial property says what the object is: it leads
  const entries = Object.entries(parameters ?? {}).sort(
    ([a], [b]) => Number(b === 'commercialType') - Number(a === 'commercialType'),
  )
  const rows: { key: string; label: string; value: string }[] = []
  const flags: { key: string; label: string }[] = []
  for (const [key, value] of entries) {
    if (value === null || value === undefined || value === false || value === '') continue
    if (Array.isArray(value)) {
      if (value.length === 0) continue
      const items = value.map((item) => translateValue(key, String(item))).join(', ')
      rows.push({ key, label: t(`step3.${key}`), value: items })
    } else if (value === true) {
      flags.push({ key, label: t(`step3.${key}`) })
    } else if (AREAS.has(key) && Number(value) > 0) {
      // an area may come as a number or as its text
      rows.push({ key, label: t(`step3.${key}`), value: t('card.area', { value: Number(value) }) })
    } else if (typeof value === 'string') {
      rows.push({ key, label: t(`step3.${key}`), value: translateValue(key, value) })
    } else if (typeof value === 'number') {
      rows.push({ key, label: t(`step3.${key}`), value: String(value) })
    }
  }

  if (rows.length === 0 && flags.length === 0) return null

  return (
    <div className="flex flex-col gap-4">
      {rows.length > 0 && (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 m-0">
          {rows.map((row) => (
            <div
              key={row.key}
              className="flex items-baseline justify-between gap-4 py-2.5 border-0 border-b border-solid border-ink/[0.08]"
            >
              <dt className="text-[0.9375rem] leading-[1.375rem] text-ink-2">{row.label}</dt>
              <dd className="m-0 text-[0.9375rem] font-bold leading-[1.375rem] text-ink text-right break-words min-w-0">
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {flags.length > 0 && (
        <ul className="flex flex-wrap gap-2 m-0 p-0 list-none">
          {flags.map((flag) => (
            <li
              key={flag.key}
              className="inline-flex items-center gap-1.5 min-h-8 px-3 py-1.5 rounded-full bg-muted-soft text-sm font-bold leading-5 text-ink"
            >
              <CheckMarkIcon className="w-4 h-4 shrink-0 text-success" />
              {flag.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default PropertyParameters
